import TrashIcon from '@old/assets/svg/core/trash-red.svg';
import { useGlobalStore } from '@old/global-store/use-store';
import { useEffectOnUpdate, useToggle } from '@old/hooks/core';
import { useService } from '@old/hooks/use-service';
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  FormControlLabel,
  FormGroup,
  GridLegacy as Grid,
  IconButton,
  Modal,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { createCountrysideZoneService } from '@old/services/domain/countryside-zone/countryside-zone-creation';
import {
  CoreErrorData,
  CountrysideZoneCreationPayload,
} from '@old/services/domain/countryside-zone/countryside-zone-creation.types';
import { deleteCountrysideZoneService } from '@old/services/domain/countryside-zone/countryside-zone-deletion';
import { CountrysideZoneDeletionPayload } from '@old/services/domain/countryside-zone/countryside-zone-deletion.types';
import { updateCountrysideZoneService } from '@old/services/domain/countryside-zone/countryside-zone-edition';
import { CountrysideZoneEditionPayload } from '@old/services/domain/countryside-zone/countryside-zone-edition.types';
import { listCountrysideZoneService } from '@old/services/domain/countryside-zone/countryside-zone-list';
import { CountrysideZone } from '@old/services/domain/countryside-zone/countryside-zone.types';
import { ToastAction } from '@old/ui/toast/toast-action';
import { useFormik } from 'formik';
import { PropsWithChildren, useRef, useState } from 'react';
import * as yup from 'yup';
import i18n from '../../../libs/language';
import { DeleteModalStandAlone } from '../modal-deletion/delete-modal-standalone';

type CountrysideZoneModalProps = {
  open: boolean;
  onClose: () => void;
  setActionInUser: (b: boolean) => void;
};

export function CountrysideZoneModal({ open, onClose, setActionInUser }: CountrysideZoneModalProps) {
  const { userOrganizationId } = useGlobalStore();
  const [shouldScrollToBottom, toggleScrollToBottom] = useToggle(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [addNew, setAddNew] = useState(false);

  const SILOBAG_ERROR_CODES: Record<number, string> = {
    20049: i18n.t('app.countryside_zone_modal.20049'),
    20057: i18n.t('app.countryside_zone_modal.20057'),
    20053: i18n.t('app.countryside_zone_modal.20053'),
    20060: i18n.t('app.countryside_zone_modal.20060'),
  };

  const init: CountrysideZone = {
    id: 0,
    name: '',
    active: true,
    created_at: '',
    updated_at: '',
    organization_id: userOrganizationId!,
  };

  const handleClose = () => {
    onClose();
    if (needRefetch.current) setActionInUser(true);
  };

  const handleSave = (countrysideZone: CountrysideZone) => {
    if (countrysideZone.id === 0) {
      const payload: CountrysideZoneCreationPayload = {
        body: {
          name: countrysideZone.name,
          active: countrysideZone.active,
          organization_id: countrysideZone.organization_id,
        },
      };

      createCountrysideZoneTrigger(payload);
    } else {
      const payload: CountrysideZoneEditionPayload = {
        query: countrysideZone.id,
        body: {
          name: countrysideZone.name,
          active: countrysideZone.active,
        },
      };

      updateCountrysideZoneTrigger(payload);
    }

    if (isError) return;

    setAddNew(false);
  };

  const handleDelete = (countrysideZone: CountrysideZone) => {
    const payload: CountrysideZoneDeletionPayload = {
      query: countrysideZone.id,
    };

    deleteCountrysideZoneTrigger(payload);
  };

  // CountrysideZone services
  const needRefetch = useRef(false);
  const {
    trigger: createCountrysideZoneTrigger,
    isLoading: isCreateCountrysideZoneLoading,
    isError: isCreateCountrysideZoneError,
    error: createCountrysideZoneError,
  } = useService(createCountrysideZoneService, {
    onSuccess: () => {
      getCountrysideList({ organizationId: userOrganizationId! });
      needRefetch.current = true;
    },
  });

  const {
    trigger: updateCountrysideZoneTrigger,
    isLoading: isUpdateCountrysideZoneLoading,
    isError: isUpdateCountrysideZoneError,
    error: updateCountrysideZoneError,
  } = useService(updateCountrysideZoneService, {
    onSuccess: () => {
      getCountrysideList({ organizationId: userOrganizationId! });
      needRefetch.current = true;
    },
  });

  const {
    trigger: deleteCountrysideZoneTrigger,
    isLoading: isDeleteCountrysideZoneLoading,
    isError: isDeleteCountrysideZoneError,
    error: deleteCountrysideZoneError,
  } = useService(deleteCountrysideZoneService, {
    onSuccess: () => getCountrysideList({ organizationId: userOrganizationId! }),
  });

  const {
    trigger: getCountrysideList,
    isLoading: isCountrysideZoneListLoading,
    summary: countrysideZoneList,
    isError: isUpdateCountrysideZoneListError,
    error: updateCountrysideZoneListError,
  } = useService(listCountrysideZoneService, { fetchOnMount: [{ organizationId: userOrganizationId! }] });

  const isLoading =
    isCreateCountrysideZoneLoading ||
    isUpdateCountrysideZoneLoading ||
    isCountrysideZoneListLoading ||
    isDeleteCountrysideZoneLoading;
  const isError =
    isCreateCountrysideZoneError ||
    isUpdateCountrysideZoneError ||
    isUpdateCountrysideZoneListError ||
    isDeleteCountrysideZoneError;

  const errorMessage = (createCountrysideZoneError ??
    updateCountrysideZoneError ??
    updateCountrysideZoneListError ??
    deleteCountrysideZoneError) as CoreErrorData | null;
  /** Services */

  useEffectOnUpdate(() => {
    if (shouldScrollToBottom && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;

      toggleScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  return (
    <MyModal open={open} onClose={handleClose}>
      <Box style={{ fontWeight: '600', fontSize: '24px' }}>{i18n.t('app.countryside_zone_modal.title')}</Box>

      {/* List Section */}
      {countrysideZoneList &&
        countrysideZoneList.map((item) => (
          <CountrysideZoneItemComponent
            key={item.id}
            countrysideZone={item}
            isLoading={isLoading}
            handleSave={handleSave}
            handleDelete={handleDelete}
          />
        ))}

      {/* Add Section */}
      {addNew && (
        <CountrysideZoneAddEdit
          data={init}
          isLoading={false}
          handleClose={() => setAddNew(false)}
          handleSave={handleSave}
        />
      )}

      {/* Modal actions section */}
      <Stack alignItems="center" style={{ width: '100%' }}>
        <Button
          style={{ width: 'max-content', textTransform: 'none' }}
          onClick={() => {
            setAddNew(true);
            toggleScrollToBottom(true);
          }}
        >
          + {i18n.t('app.countryside_zone_modal.add_zone')}
        </Button>
      </Stack>

      {isError && (
        <ToastAction
          key="invalid-file-type"
          message={
            errorMessage?.data.message.code
              ? SILOBAG_ERROR_CODES[errorMessage?.data.message.code]
              : errorMessage?.data.message.message ?? ''
          }
          severity="error"
          onClose={() => isCreateCountrysideZoneError}
        />
      )}

      {/* Loader */}
      {isLoading && <CircularProgress />}

      <Stack direction="row" spacing={1} justifyContent="flex-end" marginBlockStart={2}>
        <Button
          variant="outlined"
          style={{
            width: 115,
            borderRadius: 100,
            textTransform: 'none',
            border: 'none',
            color: 'black',
          }}
          onClick={() => handleClose()}
        >
          {i18n.t('app.close')}
        </Button>
      </Stack>
    </MyModal>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
};

function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 800,
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    overflowY: 'auto',
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style}>{children}</Box>
      </Fade>
    </Modal>
  );
}

type CountrysideZoneAddEditProps = {
  data: CountrysideZone;
  isLoading: boolean;
  handleClose: () => void;
  handleSave: (countrysideZone: CountrysideZone) => void;
};

function CountrysideZoneAddEdit({
  data: countrysideZone,
  handleSave: handleSaveCountrysideZone,
  handleClose,
  isLoading,
}: CountrysideZoneAddEditProps) {
  const validationSchema = yup.object({
    name: yup
      .string()
      .required(i18n.t('app.countryside_zone_modal.name_validation_required'))
      .min(3, i18n.t('app.countryside_zone_modal.name_validation_min_length')),
  });

  const formik = useFormik<CountrysideZone>({
    initialValues: countrysideZone,
    validationSchema: validationSchema,

    onSubmit: async (form) => {
      handleSaveCountrysideZone(formToEntity(form));
      handleClose();
    },
    onReset: () => {
      handleClose();
    },
  });

  const formToEntity = (form: CountrysideZone): CountrysideZone => {
    return {
      ...form,
    };
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
        <Grid container direction={{ xs: 'column', md: 'row' }} spacing={2} columns={4} paddingX={2}>
          <Grid item columns={2} lg={2} md={2} sm={1}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              label={i18n.t('app.countryside_zone_modal.name')}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              slotProps={{
                htmlInput: { maxLength: 45 },
              }}
            />
          </Grid>
          <Grid item xs={1}>
            <FormControlLabel
              control={
                <Switch
                  name="active" // Necesario para que Formik sepa qué campo está cambiando
                  checked={formik.values.active}
                  onChange={formik.handleChange}
                />
              }
              label={i18n.t('app.countryside_zone_modal.status_title')}
            />
          </Grid>
        </Grid>
        <Grid item>
          <Stack direction="row" spacing={1} justifyContent="flex-end" marginBlockStart={2}>
            <Button
              variant="outlined"
              type="reset"
              style={{
                width: 115,
                borderRadius: 100,
                textTransform: 'none',
                border: 'none',
                color: 'black',
              }}
            >
              {i18n.t('app.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              style={{
                color: '#FFF',
                width: 115,
                borderRadius: 100,
                textTransform: 'none',
              }}
            >
              {isLoading ? <CircularProgress style={{ width: 24, height: 24 }} /> : i18n.t('app.save')}
            </Button>
          </Stack>
        </Grid>
      </form>
    </>
  );
}

type CountrysideZoneItemComponentProps = {
  countrysideZone: CountrysideZone;
  isLoading: boolean;
  handleSave: (countrysideZone: CountrysideZone) => void;
  handleDelete: (countrysideZone: CountrysideZone) => void;
};

function CountrysideZoneItemComponent({
  countrysideZone,
  isLoading,
  handleSave,
  handleDelete,
}: CountrysideZoneItemComponentProps) {
  const [localProps, setLocalProps] = useState<{
    open?: boolean;
    countrysideZone?: CountrysideZone;
  }>({
    open: false,
  });

  return (
    <>
      {/* Edit */}
      {localProps.open && (
        <CountrysideZoneAddEdit
          data={countrysideZone}
          isLoading={isLoading}
          handleClose={() => setLocalProps({ open: false })}
          handleSave={handleSave}
        />
      )}

      {/* View */}
      {!localProps.open && (
        <CountrysideZoneComponent
          countrysideZone={countrysideZone}
          isLoading={isLoading}
          handleEdit={(data) => setLocalProps({ open: true, countrysideZone: data })}
          handleDelete={(data) => handleDelete(data)}
        />
      )}
    </>
  );
}

type CountrysideZoneProps = {
  isLoading: boolean;
  countrysideZone: CountrysideZone;
  handleEdit: (countrysideZone: CountrysideZone) => void;
  handleDelete: (countrysideZone: CountrysideZone) => void;
};

function CountrysideZoneComponent({
  handleEdit,
  handleDelete,
  countrysideZone,
  isLoading,
}: CountrysideZoneProps) {
  const { name, active } = countrysideZone;
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  return (
    <Stack key={countrysideZone.id} direction="row" spacing={4} alignItems="center">
      <FormGroup style={{ flex: 1 }}>
        <Grid container direction={{ xs: 'column', md: 'row' }} spacing={2} columns={4} paddingX={2}>
          <Grid item columns={2} lg={2} md={2} sm={1}>
            <Stack>
              <Box style={{ fontSize: '0.66rem' }}>{i18n.t('app.countryside_zone_modal.name_title')}</Box>
              {name}
            </Stack>
          </Grid>

          <Grid item xs={1}>
            <Stack>
              <Box style={{ fontSize: '0.66rem' }}>{i18n.t('app.countryside_zone_modal.status_title')}</Box>
              {active ? 'Sí' : 'No'}
            </Stack>
          </Grid>

          <Grid item xs={1}>
            <Stack key={countrysideZone.id} direction="row" spacing={1} alignItems="center">
              <Stack>
                <Button
                  onClick={() => handleEdit(countrysideZone)}
                  variant="outlined"
                  disabled={isLoading}
                  style={{
                    width: 115,
                    borderRadius: 100,
                    textTransform: 'none',
                    border: 'none',
                    color: 'black',
                  }}
                >
                  {i18n.t('app.edit')}
                </Button>
              </Stack>
              <Stack>
                <DeleteModalStandAlone
                  open={openDeleteModal}
                  setIsOpen={(value) => setOpenDeleteModal(value)}
                  targetName={countrysideZone.name}
                  handleOkEvent={() => {
                    setOpenDeleteModal(false);
                    handleDelete(countrysideZone);
                  }}
                />

                <IconButton
                  sx={{
                    color: '#F00',
                    width: 16,
                    minWidth: 16,
                    height: 16,
                    padding: 2,
                    borderRadius: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 1,
                    '&:hover': { background: '#FF000022' },
                  }}
                  onClick={() => {
                    setOpenDeleteModal(true);
                  }}
                  disabled={isLoading}
                >
                  <img src={TrashIcon} style={{ width: 16, height: 16 }} />
                </IconButton>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </FormGroup>
    </Stack>
  );
}
